import React, { useCallback, useContext } from 'react';
import { FilterStoreHook } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreScope } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { FilterStoreValue } from 'libs/end-to-end/src/filters-v2/types/filters-v2.types';
import { isSameFilterStoreScope } from 'apps/sigma2-ui/src/filters-v2/lib/isSameFilterStoreScope';
import { createFilterStoreSelection } from 'apps/sigma2-ui/src/filters-v2/lib/createFilterStoreSelection';
import { inferTypeForFilterStore } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { sanitizeFilterStoreValueBeforeSetValue } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { FilterStoreContext } from 'apps/sigma2-ui/src/filters-v2/lib/FilterStoreContext';

const cloneFilterStoreValue = (value: FilterStoreValue): FilterStoreValue => {
  const inferred = inferTypeForFilterStore(value);

  if (inferred.type === 'dateRange') {
    const { startDate, endDate, dateRangeType } = inferred.dateRange;
    return {
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      dateRangeType,
    };
  }

  if (inferred.type === 'date') {
    return new Date(inferred.date.getTime());
  }

  if (inferred.type === 'string[]') {
    return [...inferred.stringArray];
  }

  if (inferred.type === '[number, number]') {
    return [...inferred.numberRange];
  }

  return inferred.string;
};

export const useFilterStore = (): FilterStoreHook => {
  const { state, setState } = useContext(FilterStoreContext);

  const getSelectionFromStore = useCallback(
    ({ scope, key }: { scope: FilterStoreScope; key: string }) =>
      state.selections.find(
        (s) => isSameFilterStoreScope(s.scope, scope) && s.key === key,
      ),
    [state],
  );

  const getValue = useCallback(
    ({ scope, key }: { scope: FilterStoreScope; key: string }) => {
      const selection = getSelectionFromStore({
        scope,
        key,
      });

      if (!selection) {
        throw new Error('Filter not initialized');
      }

      return cloneFilterStoreValue(selection.value);
    },
    [getSelectionFromStore],
  );

  const getType = useCallback(
    ({ scope, key }: { scope: FilterStoreScope; key: string }) => {
      const selection = getSelectionFromStore({
        scope,
        key,
      });

      if (!selection) {
        throw new Error('Filter not initialized');
      }

      return selection.type;
    },
    [getSelectionFromStore],
  );

  const getInitialValue = useCallback(
    ({ scope, key }: { scope: FilterStoreScope; key: string }) => {
      const selection = getSelectionFromStore({
        scope,
        key,
      });

      if (!selection) {
        throw new Error('Filter not initialized');
      }

      return cloneFilterStoreValue(selection.initialValue);
    },
    [getSelectionFromStore],
  );

  const initialize = useCallback(
    ({
      scope,
      key,
      value,
    }: {
      scope: FilterStoreScope;
      key: string;
      value: FilterStoreValue;
    }) => {
      if (getSelectionFromStore({ scope, key })) {
        throw new Error('Filter already initialized');
      }

      setState((prevState) => ({
        selections: [
          ...prevState.selections,
          createFilterStoreSelection({
            scope,
            key,
            value: cloneFilterStoreValue(value),
            initialValue: cloneFilterStoreValue(value),
          }),
        ],
      }));
    },
    [getSelectionFromStore, setState],
  );

  const setValue = useCallback(
    ({
      scope,
      key,
      value,
    }: {
      scope: FilterStoreScope;
      key: string;
      value: FilterStoreValue;
    }) => {
      const selection = getSelectionFromStore({
        scope,
        key,
      });

      if (!selection) {
        throw new Error('Filter not initialized');
      }

      const valueSanitized = sanitizeFilterStoreValueBeforeSetValue({
        value,
        selectionType: selection.type,
      });

      setState((prevState) => {
        return {
          selections: prevState.selections.map((s) =>
            isSameFilterStoreScope(s.scope, scope) && s.key === key
              ? createFilterStoreSelection({
                  scope,
                  key,
                  value: cloneFilterStoreValue(valueSanitized),
                  initialValue: s.initialValue,
                })
              : s,
          ),
        };
      });
    },
    [getSelectionFromStore, setState],
  );

  const isInitialized = useCallback(
    ({ scope, key }: { scope: FilterStoreScope; key: string }) =>
      Boolean(getSelectionFromStore({ scope, key })),
    [getSelectionFromStore],
  );

  return {
    initialize,
    getType,
    getValue,
    setValue,
    isInitialized,
    getInitialValue,
  };
};
